import { Attachment } from '@digital-platform/attachments/domain';
import { DisposalSite } from '@digital-platform/disposal-sites/domain';
import { DocusignData } from '@digital-platform/docusign/domain';
import { Generator } from '@digital-platform/generators/domain';
import {
  PersistableEntity,
  Contact,
  MarkLogicAddress,
  MarkLogicContact,
} from '@digital-platform/shared/domain';
import { Signature, SignNowDocument } from '@digital-platform/sign-now/domain';
import { User } from '@digital-platform/users/domain';
import { InternalStatus } from './../enums/internal-status';
import { WasteProfileStatus } from '../enums/waste-profile-status';
import { AdditionalSupplement } from './additional-supplement.interface';
import { ChangeRequestDetails } from './change-request-details.interface';
import { CheckList } from './checklist.interface';
import { InternalStatusHistory } from './internal-status-log.interface';
import { RecertificationDetails } from './recertification-details.interface';
import { ThirdPartyAuthDetails } from './waste-profile-3pa.interface';
import { CommentLogHistory } from './waste-profile-comment-log.interface';
import { WasteProfileMeta } from './waste-profile-meta.interface';

export enum WasteProfileTypeLabels {
  'special-waste' = 'Special Waste',
  'hazardous' = 'Hazardous Waste',
  'deepwell' = 'Deep Well',
  'express' = 'Express Waste',
}

export interface WasteProfile extends PersistableEntity {
  // meta info
  readonly schemaVersion: 1;
  readonly type: string;
  readonly active: boolean;
  readonly version: number;
  readonly meta: WasteProfileMeta;
  readonly createdBy: string | null;
  readonly createdDate: Date;
  readonly lastModifiedBy: string | null;
  readonly lastModifiedDate: Date;
  readonly creator: Contact;
  readonly customerName: string;
  readonly status: WasteProfileStatus;
  readonly internalStatus: InternalStatus;
  readonly isLegacyProfile: boolean;
  readonly isDuplicate: boolean;
  readonly duplicatedFromId: string;
  readonly holdNotifications?: boolean;
  readonly pendingNotifications?: boolean;

  // generator & billing steps
  readonly generator: Generator;

  // waste info step
  readonly name: string;
  readonly wasteType: string;
  readonly processGeneratingWaste: string;

  // waste info or physical characteristics step
  readonly frequency: string;

  // destination step
  readonly landfill: string;
  readonly destination: DisposalSite;

  // checklist step
  readonly checkList: CheckList;

  // signature step
  readonly signNowDocUrl: string;
  readonly signNowDocument: SignNowDocument;
  readonly signatures: Signature[];
  readonly docusignData: DocusignData;
  readonly signedDocument: Attachment;
  // TODO: investigate these fields? From legacy profiles?
  readonly certificationComments: string;
  readonly certificationName: string;
  readonly certificationTitle: string;
  readonly certificationEmail: string;
  readonly certificationSignature: boolean;

  // change & recert data
  readonly pendingRecertification: RecertificationDetails;
  readonly recertificationArchive: RecertificationDetails[];
  readonly pendingChangeRequest: ChangeRequestDetails;
  readonly changeRequestArchive: ChangeRequestDetails[];

  // logbook data
  readonly number: string;
  readonly log: InternalStatusHistory[];
  readonly commentLog: CommentLogHistory[];
  readonly yearCode: string;
  readonly logbookSequenceNumber: string;
  readonly logbookId: string;
  readonly logbookNotes: string;
  readonly truxMaterialDescription: string;
  readonly approvalNotes: string;
  readonly analyticalRequiredOnNextRecert: boolean;
  readonly incompleteNotes: string;
  readonly rejectionNotes: string;
  readonly salesRepName: string;
  readonly salesRepNumber: string;
  readonly coordinator: User;
  readonly recertAnalyticalCustomerNote: string;

  // offline submission data
  readonly offlineExists: boolean;
  readonly offlineApproved: boolean;

  // status and dates
  readonly approverName: string;
  readonly incompleteBy: string;
  readonly rejectedBy: string;
  readonly submittedDate: Date;
  readonly certifiedDate: Date;
  readonly expectedResponseDate: Date;
  readonly approvedDate: Date;
  readonly rejectedDate: Date;
  readonly incompleteDate: Date;
  readonly expiryDate: Date;

  // legacy flags?
  readonly isRecertification: boolean;
  readonly isChange: boolean;
  readonly isRecertWithChange: boolean;
  readonly isExpired: boolean;

  // 3pa
  readonly thirdPartyAuth: ThirdPartyAuthDetails;
  readonly thirdPartyAuthAttachment: Attachment;

  //admin attachments
  readonly additionalAdminSupplements: AdditionalSupplement[];
  readonly thirdPartyAuthAdminAttachment: Attachment;
  readonly stateInternalAdminAttachments: Attachment[];
  readonly internalAdminAttachments: Attachment[];

  readonly stateApprovalWasteCodeNotApplicable: boolean | null;

  // unused?
  readonly additionalComments: string;
  readonly regulatoryIdNumberNotApplicable: boolean;
}

export interface DynamoSafeWasteProfile extends Omit<WasteProfile, 'number'> {
  // we are using the number field on one of the GSIs in our dynamo table, so we can't have this value be an empty string or null
  // when we go to save it to dynamo or the operation will fail. If a waste profile number has not been assigned yet, we just need
  // to remove the number field from the object before saving it to dynamo
  number?: string;
}

export interface WasteProfileV0 extends PersistableEntity {
  readonly schemaVersion?: 0;
  readonly type?: string;
  readonly id: string;
  readonly number: string;
  readonly status: string;
  readonly internalStatus: string;
  readonly divisionNumber: string;
  readonly yearCode: string;
  readonly logbookSequenceNumber: string;
  readonly logbookId: string;
  readonly logbookNotes: string;
  readonly salesRepFirstName: string;
  readonly salesRepLastName: string;
  readonly coordinatorFirstName: string;
  readonly coordinatorLastName: string;
  readonly approverFirstName: string;
  readonly approverLastName: string;
  readonly expiresOn: string;
  readonly updatedOn: string;
  readonly receivedOn: string;
  readonly approvedOn: string;
  readonly incompleteOn?: string;
  readonly rejectedOn?: string;
  readonly isOneTimeOnly: 'yes' | 'no' | '';
  readonly isRecertification: 'yes' | 'no' | '';
  readonly isChange: 'yes' | 'no' | '';
  readonly isExpired: 'yes' | 'no' | '';
  readonly parentCustomerName: string;
  readonly customerName: string;
  readonly generatorId: string;
  readonly generatorName: string;
  readonly generatorEpaId?: string;
  readonly generatorStateOrRegistrationId: string;
  readonly generatorNaics: string;
  readonly generatorIsCustomer3rdParty: boolean;
  readonly generatorIs3rdPartyAuthorized: boolean;
  readonly generatorIsOneTimeOnly: boolean;
  readonly generatorIsEditable: boolean;
  readonly generatorSiteAddress: {
    readonly address: MarkLogicAddress;
  };
  readonly generatorMailingAddress: {
    readonly address: MarkLogicAddress;
  };
  readonly generatorContact: {
    readonly contact: MarkLogicContact;
  };
  readonly billingAccountName: string;
  readonly billingAddress?: {
    readonly address: MarkLogicAddress;
  };
  readonly billingContact?: {
    readonly contact: MarkLogicContact;
  };
  readonly creatorContact?: {
    readonly contact: MarkLogicContact;
  };
  readonly wasteCode: string;
  readonly wasteStreamName: string;
  readonly wasteType: string;
  readonly wastePhysicalState: string;
  readonly hasFreeLiquids: 'yes' | 'no' | '';
  readonly solidsPercentage: string;
  readonly phRange: string;
  readonly phRangeLow: string;
  readonly phRangeHigh: string;
  readonly phExact?: string;
  readonly flashPoint: string;
  readonly wasteVolume: number;
  readonly wasteUnitOfMeasure: string;
  readonly wasteProcessDescription: string;
  readonly truxMaterialDescription: string;
  readonly disposalSiteName: string;
  readonly isLandfillDirect?: 'yes' | 'no' | '';
  readonly isSolidificationRequired: 'yes' | 'no' | '';
  readonly isBioremediationRequired: 'yes' | 'no' | '';
  readonly isDeepWell: 'yes' | 'no' | '';
  readonly isTransferFacilityRequired: 'yes' | 'no' | '';
  readonly disposalShipmentMethod: string;
  readonly attachmentLabSampleId1: string;
  readonly attachmentLabSampleDate1: string;
  readonly attachmentLabSampleType1: string;
  readonly attachmentLabisInEpaGuidelines1: 'yes' | 'no' | '';
  readonly attachmentLabSampleId2: string;
  readonly attachmentLabSampleDate2: string;
  readonly attachmentLabSampleType2: string;
  readonly attachmentLabisInEpaGuidelines2: 'yes' | 'no' | '';
  readonly attachmentSdsProductName?: string;
  readonly attachmentSdsTypicalActualPercentage?: string;
  readonly attachmentSdsLowPercentage?: string;
  readonly attachmentSdsHighPercentage?: string;
  readonly attachmentSupplementalNotes?: string;
  readonly componentName1: string;
  readonly componentTypicalActualPercentage1?: string;
  readonly componentLowPercentage1: string;
  readonly componentHighPercentage1: string;
  readonly componentName2: string;
  readonly componentTypicalActualPercentage2?: string;
  readonly componentLowPercentage2: string;
  readonly componentHighPercentage2: string;
  readonly componentName3?: string;
  readonly componentTypicalActualPercentage3?: string;
  readonly componentLowPercentage3?: string;
  readonly componentHighPercentage3?: string;
  readonly componentName4?: string;
  readonly componentTypicalActualPercentage4?: string;
  readonly componentLowPercentage4?: string;
  readonly componentHighPercentage4?: string;
  readonly componentName5?: string;
  readonly componentTypicalActualPercentage5?: string;
  readonly componentLowPercentage5?: string;
  readonly componentHighPercentage5?: string;
  readonly dominantColor: string;
  readonly otherColorDescription?: string;
  readonly odorStrength?: string;
  readonly odorDescription?: string;
  readonly rcra1: 'yes' | 'no' | '';
  readonly rcra2: 'yes' | 'no' | '';
  readonly rcra3: 'yes' | 'no' | '';
  readonly rcra4: 'yes' | 'no' | '';
  readonly rcra5: 'yes' | 'no' | '';
  readonly rcra5Attachment?: string;
  readonly rcra6: 'yes' | 'no' | '';
  readonly rcra6Notes?: string;
  readonly rcra7: 'yes' | 'no' | '';
  readonly rcra8: 'yes' | 'no' | '';
  readonly rcra9: 'yes' | 'no' | '';
  readonly rcra10: 'yes' | 'no' | '';
  readonly rcra10Notes?: string;
  readonly rep1: 'yes' | 'no' | '';
  readonly rep2: 'yes' | 'no' | '';
  readonly rep3: 'yes' | 'no' | '';
  readonly rep4: 'yes' | 'no' | '';
  readonly rep4Notes?: string;
  readonly rep4Attachment?: string;
  readonly certificationComments?: string;
  readonly certificationName: string;
  readonly certificationTitle: string;
  readonly certificationCompany?: string;
  readonly certificationEmail?: string;
  readonly certifiedOn: string;
  readonly certificationSignature: 'yes' | 'no' | '';
  readonly generatorCheckList: boolean;
  readonly billingCheckList: boolean;
  readonly wasteStreamCheckList: boolean;
  readonly attachmentsCheckList: boolean;
  readonly characteristicsCheckList: boolean;
  readonly destinationCheckList: boolean;
  readonly rcraCheckList: boolean;
}
